import React, { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "nn_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Floating "Install app" button.
 * - Chrome / Android / Edge: captures the `beforeinstallprompt` event and
 *   triggers the native install dialog on click.
 * - iOS Safari: no such event exists, so we show a toast with the manual
 *   "Add to Home Screen" steps.
 *
 * Automatically hides itself after a successful install or 7-day dismissal.
 */
export default function InstallAppButton() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        // Already installed / running standalone → nothing to show.
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (standalone) { setInstalled(true); return; }

        // Respect a recent dismissal.
        try {
            const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
            if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
        } catch { /* ignore quota */ }

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setVisible(true);
        };
        const onInstalled = () => {
            setInstalled(true);
            setVisible(false);
            setDeferredPrompt(null);
            toast.success("App installed — welcome!");
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);

        // ------------------------------------------------------------------
        // Fallback path: on any mobile device, show the button even if the
        // browser never fires `beforeinstallprompt`. Chrome's engagement
        // heuristics often delay or skip the event on first-time visitors,
        // and iOS Safari never fires it at all. Clicking the button will
        // either trigger the native prompt (if captured) or open a toast
        // with manual "Add to Home Screen" steps for the user's browser.
        // ------------------------------------------------------------------
        const ua = navigator.userAgent || "";
        const isMobile = /Android|iPad|iPhone|iPod|Mobile/.test(ua);
        const fallbackTimer = isMobile
            ? setTimeout(() => setVisible(true), 3000)
            : null;

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
            if (fallbackTimer) clearTimeout(fallbackTimer);
        };
    }, []);

    const doInstall = async () => {
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                if (outcome === "accepted") {
                    setVisible(false);
                }
                setDeferredPrompt(null);
            } catch { /* user dismissed */ }
            return;
        }
        // Fallback: browser didn't offer a native prompt — show manual
        // instructions tailored to the user's device.
        const ua = navigator.userAgent || "";
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isAndroid = /Android/.test(ua);
        if (isIOS) {
            toast("Install on iPhone", {
                description: "Tap the Share icon in Safari, then choose 'Add to Home Screen'.",
                duration: 7000,
                icon: <Smartphone className="w-4 h-4" />,
            });
        } else if (isAndroid) {
            toast("Install NiftyNews", {
                description: "Tap the ⋮ menu in Chrome, then 'Install app' or 'Add to Home Screen'.",
                duration: 7000,
                icon: <Smartphone className="w-4 h-4" />,
            });
        } else {
            toast("Install NiftyNews", {
                description: "Open this page in Chrome or Safari on your phone, then use the browser menu to Add to Home Screen.",
                duration: 7000,
                icon: <Smartphone className="w-4 h-4" />,
            });
        }
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore quota */ }
        setVisible(false);
    };

    if (installed || !visible) return null;

    return (
        <div
            className="fixed bottom-24 left-1/2 -translate-x-1/2 lg:bottom-24 lg:left-auto lg:right-6 lg:translate-x-0 z-50 flex items-stretch bg-[#0F172A] text-white rounded-full shadow-[0_14px_40px_-12px_rgba(15,23,42,0.7)] overflow-hidden"
            data-testid="install-app-cta"
        >
            <button
                type="button"
                onClick={doInstall}
                className="flex items-center gap-2 px-4 py-3 hover:bg-white/5 transition-colors"
                data-testid="install-app-button"
            >
                <Download className="w-4 h-4 text-[#22E578]" />
                <span className="font-sub text-[11px] uppercase tracking-[0.18em] font-bold">
                    Install app
                </span>
            </button>
            <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss install prompt"
                className="flex items-center justify-center px-3 border-l border-white/10 text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="install-app-dismiss"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
